import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { CreditCard, Loader2, Plus, Trash2, Save } from "lucide-react";

export default function Pagamenti() {
  const [base, setBase] = useState(null);
  const [items, setItems] = useState([]);
  const [leasingTasso, setLeasingTasso] = useState(5);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/settings/condizioni")
      .then((r) => { setBase(r.data); setItems(r.data.pagamenti || []); setLeasingTasso(r.data.leasing_tasso ?? 5); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const setRow = (i, k, v) => setItems((arr) => arr.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const remove = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const setFindomestic = (i) => setItems((arr) => arr.map((row, idx) => ({ ...row, findomestic: idx === i, leasing: idx === i ? false : row.leasing })));
  const setLeasing = (i) => setItems((arr) => arr.map((row, idx) => ({ ...row, leasing: idx === i, findomestic: idx === i ? false : row.findomestic })));

  const save = async () => {
    const p = items.map((c) => ({ titolo: (c.titolo || "").trim(), testo: (c.testo || "").trim(), findomestic: !!c.findomestic, leasing: !!c.leasing }));
    if (p.some((c) => !c.titolo || !c.testo)) { toast.error("Compila titolo e testo per ogni forma di pagamento"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/condizioni", { ...base, pagamenti: p, leasing_tasso: Number(leasingTasso) || 0 });
      setBase(data); setItems(data.pagamenti || []); setLeasingTasso(data.leasing_tasso ?? 5);
      toast.success("Pagamenti salvati");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="pagamenti-page">
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2"><CreditCard className="h-5 w-5 text-primary" strokeWidth={1.5} /></div>
          <div>
            <h1 className="text-xl font-heading font-bold">Pagamenti</h1>
            <p className="text-xs text-muted-foreground">Forme di pagamento richiamate in Outlook e nei documenti</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving || loading} data-testid="save-pagamenti-btn" className="font-semibold">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva
        </Button>
      </header>

      <div className="p-4 lg:p-8 max-w-4xl">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground mb-1">La voce marcata <b>"Findomestic"</b> viene applicata automaticamente col finanziamento attivo; la voce <b>"Leasing"</b> è applicata automaticamente per i <b>Progetti (Liv.3)</b>; le altre sono selezionabili quando il finanziamento è disattivato.</p>
            <Card className="rounded-lg border-primary/30 bg-primary/5 p-4" data-testid="leasing-tasso-card">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Tasso leasing / noleggio operativo (%) — solo Progetti (Liv.3)</Label>
              <Input type="number" step="0.01" className="max-w-[160px] mt-1" value={leasingTasso} onChange={(e) => setLeasingTasso(e.target.value)} data-testid="leasing-tasso-input" />
              <p className="text-[11px] text-muted-foreground mt-1.5">Usato al posto del finanziamento Findomestic per i lead di Livello 3 (Progetti).</p>
            </Card>
            {items.map((c, i) => (
              <Card key={i} className="rounded-lg border-border p-4" data-testid={`pagamenti-row-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 space-y-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Titolo</Label>
                      <Input value={c.titolo} onChange={(e) => setRow(i, "titolo", e.target.value)} placeholder="Bonifico bancario" data-testid={`pagamenti-titolo-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Testo</Label>
                      <Textarea value={c.testo} onChange={(e) => setRow(i, "testo", e.target.value)} placeholder="Il corrispettivo sarà corrisposto mediante…" rows={3} data-testid={`pagamenti-testo-${i}`} />
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <Switch checked={!!c.findomestic} onCheckedChange={() => setFindomestic(i)} data-testid={`pagamenti-findomestic-${i}`} />
                      <Label className="text-xs text-muted-foreground">Applica automaticamente con finanziamento Findomestic attivo</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={!!c.leasing} onCheckedChange={() => setLeasing(i)} data-testid={`pagamenti-leasing-${i}`} />
                      <Label className="text-xs text-muted-foreground">Applica automaticamente con Leasing attivo (Progetti · Liv.3)</Label>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => remove(i)} className="text-destructive hover:text-destructive shrink-0" data-testid={`pagamenti-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
            <Button variant="outline" size="sm" onClick={() => setItems((arr) => [...arr, { titolo: "", testo: "", findomestic: false, leasing: false }])} data-testid="pagamenti-add">
              <Plus className="h-4 w-4 mr-1.5" /> Aggiungi forma di pagamento
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
